import { MeiliConsolidatedBookDto } from '@hermes/domains'
import { TranslationKey } from '@hermes/translations'
import { Box, Chip, Stack, Typography, useTheme } from '@hermes/web-components'
import { useNavigation } from '@hooks/useNavigation'
import useTranslateMessage from '@hooks/useTranslateMessage'
import { useAnalytics } from '@lib/contexts/analyticsContext'
import { getVersionPriceLabel } from '@lib/utils/getVersionPriceLabel'
import { usePathname } from 'next/navigation'
import React from 'react'
import { BookCardContainer } from './Result.styles'

type SearchResultVariantsProps = {
  bookHit: MeiliConsolidatedBookDto
  setModalOpen: (arg: boolean) => void
}

type MeiliBookVariant = MeiliConsolidatedBookDto['books'][number]

export const SearchResultVariants = ({ bookHit, setModalOpen }: SearchResultVariantsProps) => {
  const theme = useTheme()
  const { t } = useTranslateMessage()
  const navigation = useNavigation()
  const analytics = useAnalytics()
  const pathname = usePathname()
  const { down } = theme.breakpoints
  const booksArraySorted = bookHit.books

  const DEVICE_CUT_OFF = 'xl'
  const MAX_VARIANTS_PER_RESULT = 2

  const onClickBookVariant = (variant: MeiliBookVariant) => {
    analytics.trackEvent('PRODUCT_CLICKED', {
      productId: variant.id,
      page: pathname || '/onClickBookVariant'
    })
    navigation.navigate.productPage(variant.slug)
  }

  const PriceChip = ({ onPress, label, disabled }: { onPress: () => void; label: string; disabled: boolean }) => (
    <Chip
      size="small"
      variant="outlined"
      sx={{
        fontSize: '0.75rem',
        minWidth: 'auto',
        textWrap: 'nowrap',
        margin: '0px',
        maxHeight: '40px'
      }}
      onClick={onPress}
      disabled={disabled}
      label={label}
    ></Chip>
  )

  const PriceChipConsolidated = ({ book }: { book: MeiliConsolidatedBookDto }) => {
    const cheapestListingLabel = getVersionPriceLabel(book.books[0])
    const cheapestLabelWithFallback = cheapestListingLabel || t({ id: 'common.sold_out', defaultMessage: 'Sold out' })
    return <PriceChip onPress={() => {}} label={cheapestLabelWithFallback} disabled={cheapestListingLabel === null} />
  }

  const PriceChipInlined = ({ book }: { book: MeiliBookVariant }) => {
    const cheapestListingLabel = getVersionPriceLabel(book)
    const cheapestLabelWithFallback = cheapestListingLabel || t({ id: 'common.sold_out', defaultMessage: 'Sold out' })
    return <PriceChip onPress={() => {}} label={cheapestLabelWithFallback} disabled={cheapestListingLabel === null} />
  }

  return (
    <Box display="flex" justifyContent="center" alignItems="center" key={booksArraySorted[0]?.id}>
      <Stack
        sx={{
          maxHeight: '70vh',
          overflowY: 'auto',
          '.list-version-item': {
            borderBottom: `1px solid ${theme.palette.divider}`,
            '&:last-child': {
              borderBottom: 'none'
            }
          },
          width: 300,
          border: `1px solid ${theme.palette.grey[300]}`,
          padding: '12px',
          [down(DEVICE_CUT_OFF)]: { display: 'none' }
        }}
        borderRadius={4}
      >
        {booksArraySorted.slice(0, MAX_VARIANTS_PER_RESULT).map((variant) => (
          <BookCardContainer
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation()
              onClickBookVariant(variant)
            }}
            key={`${variant.id}_edition_card`}
            display="flex"
            flex="1"
            alignItems="center"
            justifyContent="space-between"
            sx={{ cursor: 'pointer' }}
          >
            <Box>
              <Typography variant="caption">{`${t({
                id: `${variant.format}`,
                defaultMessage: 'Format unavailable'
              })} ·  ${variant.year || ''}`}</Typography>

              {variant.languages[0] !== undefined && (
                <Typography variant="subtitle2">
                  {t({ id: variant.languages[0] as TranslationKey, defaultMessage: 'Language unavailable' })}
                </Typography>
              )}
            </Box>
            <PriceChipInlined book={variant} />
          </BookCardContainer>
        ))}

        {booksArraySorted.length > MAX_VARIANTS_PER_RESULT && (
          <Box
            onClick={() => setModalOpen(true)}
            display="flex"
            style={{ padding: '12px 0', cursor: 'pointer' }}
            justifyContent={'center'}
          >
            <Typography variant="button" sx={{ fontSize: '1.05rem' }}>{`${t({
              id: 'bookDetails.OtherVersions',
              defaultMessage: 'Other versions'
            })} · ${booksArraySorted.length}`}</Typography>
          </Box>
        )}
      </Stack>
      <Stack sx={{ display: 'none', [theme.breakpoints.down(DEVICE_CUT_OFF)]: { display: 'flex' } }}>
        <PriceChipConsolidated book={bookHit} />
      </Stack>
    </Box>
  )
}
